import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import axios from 'axios';
import { Printer, Link2, Check, FileText, ArrowLeft, MessageCircle, Send, Loader2 } from 'lucide-react';
import { captureUtmFromUrl, getStoredUtm } from '../lib/utm';
import './MerchantKit.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MerchantKit() {
  const { slug: routeSlug } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState('');

  // Lead capture state
  const [lead, setLead] = useState({ name: '', supermarket_name: '', phone: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const activeSlug = routeSlug || params.get('doc') || 'all';

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/merchant-kit/docs`)
      .then(({ data }) => { if (!cancelled) setDocs(data.docs || []); })
      .catch((err) => console.error('Failed to load merchant kit', err))
      .finally(() => { if (!cancelled) setLoading(false); });
    axios.get(`${API}/settings`)
      .then(({ data }) => { if (!cancelled) setWhatsappNumber((data?.whatsapp_number || '').replace(/[^0-9+]/g, '')); })
      .catch((err) => console.error('Failed to load settings', err));

    // Fire an anonymized pageview beacon (deduped per session + path server-side).
    try {
      captureUtmFromUrl();
      const utm = getStoredUtm();
      let sid = localStorage.getItem('mk_session');
      if (!sid) {
        sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
        localStorage.setItem('mk_session', sid);
      }
      axios.post(`${API}/merchant-kit/pageview`, {
        path: window.location.pathname,
        session_id: sid,
        referrer: document.referrer || '',
        ...utm,
      }).catch(() => { /* ignore */ });
    } catch (e) { /* ignore */ }

    return () => { cancelled = true; };
  }, [routeSlug]);

  const visible = useMemo(() => {
    if (activeSlug === 'all') return docs;
    return docs.filter((d) => d.slug === activeSlug);
  }, [docs, activeSlug]);

  const handlePrint = () => window.print();

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed', err);
    }
  };

  const setActive = (slug) => {
    if (slug === 'all') {
      setParams({});
    } else {
      setParams({ doc: slug });
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const submitLead = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (!lead.name.trim() || !lead.supermarket_name.trim() || !lead.phone.trim()) {
      setSubmitError('Name, supermarket, and phone are required.');
      return;
    }
    setSubmitting(true);
    try {
      const utm = getStoredUtm();
      await axios.post(`${API}/merchant-kit/leads`, { ...lead, ...utm });
      setSubmitSuccess(true);
      setLead({ name: '', supermarket_name: '', phone: '', email: '', message: '' });
    } catch (err) {
      setSubmitError(err.response?.data?.detail || 'Could not submit. Please try again or WhatsApp us.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="mk-page"><div className="mk-loading">Loading Merchant Kit…</div></div>;
  }

  return (
    <div className="mk-page" data-testid="merchant-kit-page">
      <Helmet>
        <title>StoreRuns Merchant Kit — 30-day free trial for supermarkets</title>
        <meta name="description" content="A ready-to-launch, branded online store for your supermarket. Multi-branch, WhatsApp-powered delivery, automatic distance-based delivery fees. 30-day free trial. No commission. Cancel any time." />
        <link rel="canonical" href="https://storeruns.online/merchant-kit" />
        <meta property="og:type" content="article" />
        <meta property="og:title" content="StoreRuns Merchant Kit — 30-day free trial for supermarkets" />
        <meta property="og:description" content="Launch your branded online store in 48 hours. 30-day free trial, no commission, cancel any time." />
        <meta property="og:url" content="https://storeruns.online/merchant-kit" />
        <meta property="og:image" content="https://storeruns.online/og-merchant-kit.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="StoreRuns Merchant Kit — 30-day free trial for supermarkets" />
        <meta name="twitter:description" content="Launch your branded online store in 48 hours. 30-day free trial, no commission, cancel any time." />
        <meta name="twitter:image" content="https://storeruns.online/og-merchant-kit.jpg" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "serviceType": "Online grocery delivery platform",
          "provider": {
            "@type": "Organization",
            "name": "StoreRuns",
            "url": "https://storeruns.online",
            "logo": "https://storeruns.online/icon-512.png"
          },
          "areaServed": "Zambia",
          "description": "StoreRuns launches branded online grocery storefronts for supermarkets in 48 hours. 30-day free trial, no commission, cancel any time.",
          "offers": {
            "@type": "Offer",
            "name": "30-day Free Trial",
            "price": "0",
            "priceCurrency": "ZMW",
            "description": "Free 30-day trial for supermarkets. K150 per branch per month thereafter, no commission per order."
          },
          "audience": {
            "@type": "BusinessAudience",
            "audienceType": "Supermarket owners"
          }
        })}</script>
      </Helmet>
      <div className="mk-header no-print">
        <button className="mk-back" onClick={() => navigate('/')} data-testid="mk-back-btn">
          <ArrowLeft size={16} /> Back to storefront
        </button>
        <div className="mk-header-inner">
          <div>
            <h1 className="mk-title">StoreRuns Merchant Kit</h1>
            <p className="mk-subtitle">
              Everything a supermarket owner needs to say yes — terms, pitch, scripts and staff quick-start.
              Share this link, or use <strong>Print / Save as PDF</strong> below.
            </p>
          </div>
          <div className="mk-actions">
            <button className="mk-btn mk-btn-primary" onClick={handlePrint} data-testid="mk-print-btn">
              <Printer size={16} /> Print / Save as PDF
            </button>
            <button className="mk-btn mk-btn-ghost" onClick={handleCopyLink} data-testid="mk-copy-link-btn">
              {copied ? <Check size={16} /> : <Link2 size={16} />}
              {copied ? 'Link copied!' : 'Copy shareable link'}
            </button>
          </div>
        </div>

        <div className="mk-tabs" role="tablist">
          <button
            className={`mk-tab ${activeSlug === 'all' ? 'active' : ''}`}
            onClick={() => setActive('all')}
            data-testid="mk-tab-all"
          >
            <FileText size={14} /> Full kit
          </button>
          {docs.map((d) => (
            <button
              key={d.slug}
              className={`mk-tab ${activeSlug === d.slug ? 'active' : ''}`}
              onClick={() => setActive(d.slug)}
              data-testid={`mk-tab-${d.slug}`}
            >
              {d.title}
            </button>
          ))}
        </div>
      </div>

      <div className="mk-docs">
        {activeSlug === 'all' && (
          <section className="mk-lead-form no-print" data-testid="mk-lead-form">
            {submitSuccess ? (
              <div className="mk-lead-success" data-testid="mk-lead-success">
                <div className="mk-lead-check"><Check size={28} /></div>
                <h2>Thanks! We&apos;ll be in touch shortly.</h2>
                <p>Your details are with us. Expect a WhatsApp message within one business day. In the meantime, feel free to browse the full kit below.</p>
                <button className="mk-btn mk-btn-ghost" onClick={() => setSubmitSuccess(false)} data-testid="mk-lead-reset-btn">
                  Submit another
                </button>
              </div>
            ) : (
              <>
                <div className="mk-lead-header">
                  <span className="mk-lead-eyebrow">Interested in a 30-day free trial?</span>
                  <h2>Tell us about your supermarket — we&apos;ll get back to you within one business day.</h2>
                  <p>No credit card. No commitment. Just a friendly chat.</p>
                </div>
                <form onSubmit={submitLead} className="mk-lead-fields">
                  <div className="mk-lead-row">
                    <label className="mk-lead-field">
                      <span>Your name*</span>
                      <input
                        type="text"
                        value={lead.name}
                        onChange={(e) => setLead({ ...lead, name: e.target.value })}
                        placeholder="e.g. John Banda"
                        required
                        maxLength={120}
                        data-testid="mk-lead-name"
                      />
                    </label>
                    <label className="mk-lead-field">
                      <span>Supermarket name*</span>
                      <input
                        type="text"
                        value={lead.supermarket_name}
                        onChange={(e) => setLead({ ...lead, supermarket_name: e.target.value })}
                        placeholder="e.g. Sunrise Groceries"
                        required
                        maxLength={120}
                        data-testid="mk-lead-store"
                      />
                    </label>
                  </div>
                  <div className="mk-lead-row">
                    <label className="mk-lead-field">
                      <span>WhatsApp / phone*</span>
                      <input
                        type="tel"
                        value={lead.phone}
                        onChange={(e) => setLead({ ...lead, phone: e.target.value })}
                        placeholder="e.g. +260 97 123 4567"
                        required
                        maxLength={40}
                        data-testid="mk-lead-phone"
                      />
                    </label>
                    <label className="mk-lead-field">
                      <span>Email (optional)</span>
                      <input
                        type="email"
                        value={lead.email}
                        onChange={(e) => setLead({ ...lead, email: e.target.value })}
                        placeholder="you@yourstore.com"
                        maxLength={200}
                        data-testid="mk-lead-email"
                      />
                    </label>
                  </div>
                  <label className="mk-lead-field">
                    <span>Anything you&apos;d like us to know? (optional)</span>
                    <textarea
                      rows={3}
                      value={lead.message}
                      onChange={(e) => setLead({ ...lead, message: e.target.value })}
                      placeholder="Number of branches, timings, questions..."
                      maxLength={1000}
                      data-testid="mk-lead-message"
                    />
                  </label>
                  {submitError && <div className="mk-lead-error" data-testid="mk-lead-error">{submitError}</div>}
                  <button
                    type="submit"
                    className="mk-btn mk-btn-primary mk-lead-submit"
                    disabled={submitting}
                    data-testid="mk-lead-submit"
                  >
                    {submitting ? <Loader2 size={16} className="mk-spin" /> : <Send size={16} />}
                    {submitting ? 'Sending...' : 'Request my free trial'}
                  </button>
                </form>
              </>
            )}
          </section>
        )}

        {visible.map((doc, idx) => (
          <article
            key={doc.slug}
            className="mk-doc"
            data-testid={`mk-doc-${doc.slug}`}
            style={{ pageBreakAfter: idx < visible.length - 1 ? 'always' : 'auto' }}
          >
            <div className="mk-doc-meta no-print">
              <span className="mk-doc-badge">{String(idx + 1).padStart(2, '0')}</span>
              <span className="mk-doc-summary">{doc.summary}</span>
            </div>
            <div className="mk-markdown">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{doc.body}</ReactMarkdown>
            </div>
          </article>
        ))}
      </div>

      <footer className="mk-footer no-print">
        <p>StoreRuns Merchant Kit · Shareable at <code>{window.location.origin}/merchant-kit</code></p>
      </footer>

      {whatsappNumber && (
        <a
          href={`https://wa.me/${whatsappNumber.replace(/^\+/, '')}?text=${encodeURIComponent("Hi StoreRuns 👋 I read your Merchant Kit and I'd like to learn more about the 30-day free trial for my supermarket.")}`}
          target="_blank"
          rel="noopener noreferrer"
          className="mk-whatsapp-cta no-print"
          data-testid="mk-whatsapp-cta"
        >
          <MessageCircle size={18} />
          <span className="mk-whatsapp-cta-text">Interested? Text us on WhatsApp</span>
          <span className="mk-whatsapp-cta-short">WhatsApp us</span>
        </a>
      )}
    </div>
  );
}
